/**
 * Coalesced fetch for `GET /api/session/current`.
 *
 * On load the app resolves the session from several independent places
 * concurrently — the App bootstrap (`checkSession`), `TenantContext`, and
 * `ImpersonationContext`. Each used to fire its own request, so a single page
 * load hit the endpoint 3-4×, and every hit triggered the BFF's user-profile +
 * consent + membership/tenant-flag fan-out.
 *
 * This shares a single in-flight request between concurrent callers: while a
 * request is pending, additional callers receive a clone of the same Response.
 * Once it settles the in-flight handle is cleared, so a later call (an explicit
 * refresh after consent, or a subsequent page load) fetches fresh — there is no
 * time-based staleness, only de-duplication of the concurrent burst.
 */
let inFlight: Promise<Response> | null = null;

export function fetchCurrentSession(): Promise<Response> {
  if (!inFlight) {
    const pending = fetch('/api/session/current', { credentials: 'include' });
    inFlight = pending;
    // Release the shared handle once settled so the next non-concurrent call
    // refetches. The no-op catch keeps this housekeeping chain from surfacing
    // as an unhandled rejection; real callers handle errors on their own chain.
    pending
      .finally(() => {
        if (inFlight === pending) inFlight = null;
      })
      .catch(() => undefined);
  }
  // Clone so each concurrent caller gets an independently-readable body. Real
  // fetch always supports clone(); the guard lets simple test fetch-mocks
  // (single reader) pass the response through unchanged.
  return inFlight.then((response) =>
    typeof response.clone === 'function' ? response.clone() : response,
  );
}
